import React, { useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { toast } from 'react-toastify'

import { apiUrl } from '../../api'
import Colors from '../../assets/styles/Colors'
import type { AIChatMessage } from '../../domain/types'

// 🎨 Lucide Icons
import { ArrowUp, File, Images, MessageSquarePlus, MessagesSquare, Paperclip, Plus, X } from 'lucide-react'

// 📝 Markdown & Syntax Highlighting
import ReactMarkdown from 'react-markdown'
import SyntaxHighlighter from 'react-syntax-highlighter/dist/cjs/prism'
import { vscDarkPlus } from 'react-syntax-highlighter/dist/cjs/styles/prism'

import rehypeRaw from 'rehype-raw'
import rehypeResponsiveTables from 'rehype-responsive-tables'
import remarkGfm from 'remark-gfm'

// 🎨 Styled Components imported from a separate file
import { useTranslation } from 'react-i18next'
import AIChats from './AI-chats'
import AILibrary from './AI-library'
import {
  AIMessageCard,
  AIMessageContent,
  AttachmentBadge,
  AttachmentList,
  ChatArea,
  ChatAttachmentBadge,
  ChatAttachmentCard,
  ChatAttachmentIcon,
  ChatAttachmentInfo,
  ChatAttachmentList,
  ChatAttachmentName,
  Container,
  Disclaimer,
  Dot,
  IconButton,
  Input,
  InputContainer,
  InputWrapper,
  List,
  ListItem,
  MenuDivider,
  MenuDropdown,
  MenuItem,
  MenuItemIcon,
  MenuItemText,
  MenuWrapper,
  MessageGroup,
  OrderedList,
  ParagraphStyled,
  SectionTitle,
  SendButton,
  SlideContainer,
  SlideView,
  TitleIcon,
  TypingIndicatorWrapper,
  UserMessage,
  UserMessageWrapper,
} from './AIAssistant.styles'

// ═══════════════════════════════════════════════════════════════════════════════════════════════
// TYPESCRIPT INTERFACES
// ═══════════════════════════════════════════════════════════════════════════════════════════════

/**
 * Represents a single chat message in the conversation
 */
interface ChatMessage {
  message: string // The text content of the message
  sender: 'user' | 'bot' // Who sent the message
  direction: 'incoming' | 'outgoing' // Direction of the message
  attachments?: Array<{ name: string; path: string }>
}

/**
 * Props for the CustomMarkdownRenderer component
 */
interface CustomMarkdownProps {
  content: string // Markdown content to render
}

// ═══════════════════════════════════════════════════════════════════════════════════════════════
// CONSTANTS & CONFIGURATION
// ═══════════════════════════════════════════════════════════════════════════════════════════════

/**
 * File extensions allowed for upload
 * Documents: pdf, docx, doc, md
 * Images: png, jpg, jpeg
 */
const ALLOWED_EXTENSIONS = ['pdf', 'docx', 'doc', 'md', 'png', 'jpg', 'jpeg']

/**
 * MIME types for the file input accept attribute
 * This limits what file types users can select in the file picker dialog
 */
const ACCEPTED_MIME_TYPES = [
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // docx
  'application/msword', // doc
  'text/markdown', // md
  'image/png', // png
  'image/jpeg', // jpg, jpeg
].join(',')

/**
 * Maximum number of files that can be uploaded at once
 */
const MAX_FILES_PER_UPLOAD = 5

/**
 * Maximum allowed file size (in MB and bytes)
 */
const MAX_FILE_SIZE_MB = 10
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024

/**
 * Delay in milliseconds between streaming text updates for smooth animation
 */
const STREAMING_DELAY_MS = 50

// ═══════════════════════════════════════════════════════════════════════════════════════════════
// UTILITY FUNCTIONS
// ═══════════════════════════════════════════════════════════════════════════════════════════════

/**
 * Creates a delay for the specified number of milliseconds
 * Used to create smooth streaming animation effect
 */
const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

/**
 * Generates a unique conversation ID for tracking chat sessions
 * Format: chat_{timestamp}_{randomString}
 * Example: chat_1730332800123_k9x2m5p8q1r
 * @returns A unique conversation identifier
 */
const generateConversationId = (): string => {
  const timestamp = Date.now()
  const randomString = Math.random().toString(36).substring(2, 15)
  return `chat_${timestamp}_${randomString}`
}

/**
 * Saves the conversation ID to localStorage
 * @param conversationId - The conversation ID to save
 */
const saveConversationId = (conversationId: string) => {
  if (conversationId) {
    localStorage.setItem('ai_last_conversation_id', conversationId)
  } else {
    localStorage.removeItem('ai_last_conversation_id')
  }
}

/**
 * Gets the last conversation ID from localStorage
 * @returns The saved conversation ID or null
 */
const getLastConversationId = (): string | null => {
  return localStorage.getItem('ai_last_conversation_id')
}

const getFileExtension = (fileName: string) => {
  if (!fileName) return ''
  const sanitized = fileName.split('?')[0]
  const parts = sanitized.split('.')
  if (parts.length < 2) return ''
  return parts.pop()?.toUpperCase() ?? ''
}

/**
 * Extracts file names from an array of file URLs
 * @param urls - Array of file URLs
 * @returns Comma-separated string of file names
 */

// ═══════════════════════════════════════════════════════════════════════════════════════════════
// TABLE COMPONENT HELPERS
// ═══════════════════════════════════════════════════════════════════════════════════════════════

// ChatGPT-style table CSS
const chatGPTTableStyles = `
  .chatgpt-table-wrapper table {
    border-collapse: collapse;
    width: 100%;
    margin-bottom: 20px;
  }
  .chatgpt-table-wrapper th,
  .chatgpt-table-wrapper td {
    border: 1px solid #ddd;
    padding: 8px;
    text-align: left;
  }
  .chatgpt-table-wrapper th {
    background-color: #f2f2f2;
    font-weight: 600;
  }
  .chatgpt-table-wrapper tbody tr:nth-child(even) {
    background-color: #f9f9f9;
  }
  .chatgpt-table-wrapper tbody tr:nth-child(odd) {
    background-color: transparent;
  }
`

const TableWrapper = ({ children, node, ...props }: any) => (
  <>
    <style>{chatGPTTableStyles}</style>
    <div
      className="chatgpt-table-wrapper"
      style={{
        overflowX: 'auto',
        marginTop: '1rem',
        marginBottom: '1.25rem',
      }}
    >
      <table {...props}>{children}</table>
    </div>
  </>
)

const TableHead = ({ children, node, ...props }: any) => <thead {...props}>{children}</thead>
const TableBody = ({ children, node, ...props }: any) => <tbody {...props}>{children}</tbody>

const TableRow = ({ children, node, ...props }: any) => {
  // Filter out empty rows - check if row has any valid content
  const hasContent = React.Children.toArray(children).some((child: any) => {
    if (!child) return false
    if (typeof child === 'string') return child.trim().length > 0
    if (React.isValidElement(child)) {
      const childProps = child.props as any
      const childContent = childProps?.children
      if (typeof childContent === 'string') return childContent.trim().length > 0
      if (Array.isArray(childContent)) {
        return childContent.some((c: any) => {
          if (typeof c === 'string') return c.trim().length > 0
          return c != null
        })
      }
      return childContent != null
    }
    return false
  })

  // Don't render empty rows
  if (!hasContent) {
    return null
  }

  return <tr {...props}>{children}</tr>
}

const TableHeader = ({ children, node, ...props }: any) => (
  <th
    style={{
      whiteSpace: 'normal',
      wordBreak: 'break-word',
    }}
    {...props}
  >
    {children}
  </th>
)

const TableCell = ({ children, node, ...props }: any) => (
  <td
    style={{
      verticalAlign: 'top',
      whiteSpace: 'normal',
      wordBreak: 'break-word',
    }}
    {...props}
  >
    {children}
  </td>
)

// Code block renderer with syntax highlighting
const CodeBlock = ({ node, inline, className, children, ...props }: any) => {
  const match = /language-(\w+)/.exec(className || '')
  const isInline = !!inline

  // Use syntax highlighter for code blocks, regular code tag for inline code
  if (!isInline && match) {
    return (
      <SyntaxHighlighter style={vscDarkPlus} language={match[1]} PreTag="div" {...props}>
        {String(children).replace(/\n$/, '')}
      </SyntaxHighlighter>
    )
  }
  return (
    <code className={className} {...(props as React.HTMLAttributes<HTMLElement>)}>
      {children}
    </code>
  )
}

// Heading component
const Heading2 = ({ children }: any) => (
  <SectionTitle>
    <TitleIcon />
    {children}
  </SectionTitle>
)

// Paragraph component
const Paragraph = ({ children }: any) => <ParagraphStyled>{children}</ParagraphStyled>

// List components
const UnorderedList = ({ children }: any) => <List>{children}</List>
const ListItemComponent = ({ children }: any) => <ListItem>{children}</ListItem>
const OrderedListComponent = ({ children }: any) => <OrderedList>{children}</OrderedList>

// ═══════════════════════════════════════════════════════════════════════════════════════════════
// MARKDOWN RENDERER COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════════════════════

/**
 * CustomMarkdownRenderer
 *
 * Renders markdown content with custom styling and syntax highlighting.
 * Supports:
 * - GitHub Flavored Markdown (GFM)
 * - Code syntax highlighting with Prism
 * - Tables, lists, headings
 * - Raw HTML content
 */
const CustomMarkdownRenderer: React.FC<CustomMarkdownProps> = ({ content }) => {
  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]} // GitHub Flavored Markdown support (includes table parsing)
      rehypePlugins={[rehypeRaw, rehypeResponsiveTables]} // Allow raw HTML and responsive tables
      components={{
        // Custom heading renderer with icon
        h2: Heading2,

        // Custom paragraph styling
        p: Paragraph,

        // List styling
        ul: UnorderedList,
        li: ListItemComponent,
        ol: OrderedListComponent,

        // Table styling with borders and padding
        table: TableWrapper,
        thead: TableHead,
        tbody: TableBody,
        tr: TableRow,
        th: TableHeader,
        td: TableCell,

        // Code block renderer with syntax highlighting
        code: CodeBlock,
      }}
    >
      {content}
    </ReactMarkdown>
  )
}

const MessageAttachments: React.FC<{
  attachments?: Array<{ name: string; path: string }>
  align?: 'left' | 'right'
}> = ({ attachments, align = 'left' }) => {
  if (!attachments || attachments.length === 0) {
    return null
  }

  return (
    <ChatAttachmentList align={align}>
      {attachments.map((attachment, index) => {
        const extension = getFileExtension(attachment.name)
        return (
          <ChatAttachmentCard
            key={`${attachment.path}-${index}`}
            href={attachment.path}
            target="_blank"
            rel="noopener noreferrer"
            download={attachment.name}
          >
            <ChatAttachmentIcon>
              <File size={18} color={Colors.primaryRed} />
            </ChatAttachmentIcon>
            <ChatAttachmentInfo>
              <ChatAttachmentName>{attachment.name}</ChatAttachmentName>
            </ChatAttachmentInfo>
            {extension ? <ChatAttachmentBadge>{extension}</ChatAttachmentBadge> : null}
          </ChatAttachmentCard>
        )
      })}
    </ChatAttachmentList>
  )
}

// ═══════════════════════════════════════════════════════════════════════════════════════════════
// MESSAGE DISPLAY COMPONENTS
// ═══════════════════════════════════════════════════════════════════════════════════════════════

/**
 * AIAssistantMessage
 *
 * Displays a message from the AI assistant with markdown rendering
 */
const AIAssistantMessage: React.FC<{ message: ChatMessage }> = ({ message }) => (
  <MessageGroup>
    <AIMessageCard>
      <MessageAttachments attachments={message.attachments} />
      {message.message ? (
        <AIMessageContent>
          <CustomMarkdownRenderer content={message.message} />
        </AIMessageContent>
      ) : null}
    </AIMessageCard>
  </MessageGroup>
)

/**
 * UserAssistantMessage
 *
 * Displays a message from the user
 */
const UserAssistantMessage: React.FC<{ message: ChatMessage }> = ({ message }) => (
  <UserMessageWrapper>
    <MessageAttachments attachments={message.attachments} align="right" />
    {message.message ? <UserMessage>{message.message}</UserMessage> : null}
  </UserMessageWrapper>
)

// ═══════════════════════════════════════════════════════════════════════════════════════════════
// MAIN AI ASSISTANT COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════════════════════

/**
 * AIAssistant
 *
 * Main component for the AI chat assistant interface.
 *
 * Features:
 * - Real-time streaming responses from AI
 * - File attachment support (documents and images)
 * - Conversation context management
 * - Markdown formatting in AI responses
 * - Typing indicators
 * - File upload validation
 *
 * Flow:
 * 1. User types a message or attaches files
 * 2. Message is sent to API with conversation ID
 * 3. AI response streams back in real-time
 * 4. Response is rendered with markdown formatting
 * 5. Context is maintained across messages in same conversation
 */
const AIAssistant: React.FC = () => {
  // ─────────────────────────────────────────────────────────────────────────────────────────────
  // HOOKS & SELECTORS
  // ─────────────────────────────────────────────────────────────────────────────────────────────

  const token = useSelector((state: { auth: { token: string | null } }) => state.auth.token) // Auth token from Redux

  // ─────────────────────────────────────────────────────────────────────────────────────────────
  // STATE MANAGEMENT
  // ─────────────────────────────────────────────────────────────────────────────────────────────

  // Chat messages array
  const [messages, setMessages] = useState<ChatMessage[]>([])

  // Loading states
  const [isWaitingForResponse, setIsWaitingForResponse] = useState(false) // Waiting for AI response
  const [isUploading, setIsUploading] = useState(false) // File upload in progress
  const isStreamingRef = useRef(false) // Track if currently streaming to prevent useEffect scroll conflicts

  // Input state
  const [inputMessage, setInputMessage] = useState('') // Current message being typed

  // Conversation tracking - initialize with saved ID or generate new one
  const [conversationId, setConversationId] = useState<string>(() => {
    const savedId = getLastConversationId()
    // If we have a saved ID, use it (will be loaded in useEffect if logged in)
    // If not, generate new one but don't save yet (will be saved in useEffect)
    return savedId || generateConversationId()
  })
  const [previousResponseId, setPreviousResponseId] = useState<string | null>(null) // ID of last AI response for context

  // File attachments
  const [attachedFiles, setAttachedFiles] = useState<Array<{ name: string; path: string }>>([]) // Uploaded files with name and path

  // Menu state
  const [isMenuOpen, setIsMenuOpen] = useState(false) // Controls menu visibility

  // View state - tracks whether to show main chat or chats list
  const [showChats, setShowChats] = useState(false) // Controls which view is displayed
  const [showLibrary, setShowLibrary] = useState(false) // Controls library view display
  const [expandedView, setExpandedView] = useState<'chats' | 'library' | null>(null) // Which view to show when expanded
  const [isExpanded, setIsExpanded] = useState(false) // Whether the dropdown is expanded
  const [hasDroppedDown, setHasDroppedDown] = useState(false) // Track if input has ever dropped down
  const { t } = useTranslation()
  const hasReachedAttachmentLimit = attachedFiles.length >= MAX_FILES_PER_UPLOAD

  const resetCurrentConversation = () => {
    setMessages([])
    setPreviousResponseId(null)
    const newId = generateConversationId()
    setConversationId(newId)
    saveConversationId(newId)
    setAttachedFiles([])
    setInputMessage('')
  }

  // ─────────────────────────────────────────────────────────────────────────────────────────────
  // REFS
  // ─────────────────────────────────────────────────────────────────────────────────────────────

  const fileInputRef = useRef<HTMLInputElement>(null) // Reference to hidden file input element
  const textareaRef = useRef<HTMLTextAreaElement>(null) // Reference to textarea for resetting height
  const chatEndRef = useRef<HTMLDivElement>(null) // Reference to bottom of chat for auto-scrolling
  const chatAreaRef = useRef<HTMLDivElement>(null) // Reference to chat area container for controlled scrolling
  const menuRef = useRef<HTMLDivElement>(null) // Reference to menu wrapper for click-outside detection

  // ─────────────────────────────────────────────────────────────────────────────────────────────
  // UTILITY FUNCTIONS
  // ─────────────────────────────────────────────────────────────────────────────────────────────

  /**
   * Scrolls the chat area to the bottom
   * Uses controlled scrolling for smoother behavior during streaming
   * @param behavior - 'smooth' for animated scroll, 'auto' for instant
   */
  const scrollToBottom = (behavior: ScrollBehavior = 'smooth') => {
    if (chatAreaRef.current) {
      // Use scrollTop for more controlled scrolling
      const scrollContainer = chatAreaRef.current
      if (behavior === 'smooth') {
        scrollContainer.scrollTo({
          top: scrollContainer.scrollHeight,
          behavior: 'smooth',
        })
      } else {
        // For 'auto' behavior, use scrollTop directly for instant scroll
        scrollContainer.scrollTop = scrollContainer.scrollHeight
      }
    } else {
      // Fallback to scrollIntoView if ref is not available
      chatEndRef.current?.scrollIntoView({ behavior })
    }
  }

  /**
   * Starts a new chat session
   * - Clears all messages
   * - Generates new conversation ID
   * - Resets all state
   *
   * Use this when user wants to start fresh without previous context
   */
  const startNewChat = () => {
    setMessages([])
    setPreviousResponseId(null)
    const newId = generateConversationId()
    setConversationId(newId)
    saveConversationId(newId)
    setAttachedFiles([])
    setInputMessage('')
    setExpandedView(null) // Reset expanded view so it doesn't auto-expand on focus
    setShowChats(false)
    setShowLibrary(false)
    setIsExpanded(false)
  }

  /**
   * Loads a chat conversation by conversationId
   * - Fetches chat messages from the API
   * - Maps backend response to ChatMessage format
   * - Sets conversation state (conversationId, previousResponseId, messages)
   * - Closes chats view and shows main chat
   *
   * @param conversationId - The conversation ID to load
   * @param silent - If true, suppresses error messages (useful when loading from localStorage)
   */
  const loadChat = async (conversationIdToLoad: string, silent: boolean = false) => {
    try {
      const res = await fetch(`${apiUrl}ai/assistant/chats/${conversationIdToLoad}`, {
        method: 'GET',
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      })

      if (!res.ok) {
        throw new Error('Failed to load chat')
      }

      const chatMessages: AIChatMessage[] = await res.json()

      if (!chatMessages || chatMessages.length === 0) {
        if (!silent) {
          toast.error('Chat not found')
        }
        return
      }

      // Map backend response to ChatMessage format
      const mappedMessages: ChatMessage[] = []

      chatMessages.forEach((chatItem: AIChatMessage) => {
        const attachmentsFromMeta = chatItem.metaData?.attachments ?? []

        // Add user message
        if (chatItem.question) {
          mappedMessages.push({
            message: chatItem.question,
            sender: 'user',
            direction: 'outgoing',
            attachments: attachmentsFromMeta.length > 0 ? attachmentsFromMeta : undefined,
          })
        }

        // Add bot message
        if (chatItem.answer) {
          mappedMessages.push({
            message: chatItem.answer,
            sender: 'bot',
            direction: 'incoming',
          })
        }
      })

      // Set the last previousResponseId from the last message
      const lastMessage = chatMessages[chatMessages.length - 1]
      const lastPreviousResponseId = lastMessage?.previousResponseId || null

      // Update state
      setMessages(mappedMessages)
      setConversationId(conversationIdToLoad)
      saveConversationId(conversationIdToLoad) // Save to localStorage
      setPreviousResponseId(lastPreviousResponseId)
      setAttachedFiles([])
      setInputMessage('')

      // Close chats view and show main chat
      setShowChats(false)
      setShowLibrary(false)
      setIsExpanded(false)

      // Scroll to bottom after a short delay to ensure DOM is updated
      setTimeout(() => {
        scrollToBottom('auto')
      }, 100)
    } catch (error) {
      console.info('Error loading chat:', error)
      if (!silent) {
        toast.error('Failed to load chat. Please try again.')
      }
      throw error // Re-throw to allow caller to handle silently
    }
  }

  // ─────────────────────────────────────────────────────────────────────────────────────────────
  // FILE UPLOAD HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────────────────────

  /**
   * Handles file selection from file input
   *
   * Validation steps:
   * 1. Check if files exist
   * 2. Validate file count (max 5)
   * 3. Validate file types (only allowed extensions)
   * 4. Upload valid files
   *
   * @param event - File input change event
   */
  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files
    if (!files || files.length === 0) return

    const remainingSlots = MAX_FILES_PER_UPLOAD - attachedFiles.length
    if (remainingSlots <= 0) {
      toast.warn(`You can attach up to ${MAX_FILES_PER_UPLOAD} files per message.`)
      if (fileInputRef.current) fileInputRef.current.value = ''
      return
    }

    // Step 1: Validate file count
    const selectedFiles = Array.from(files).slice(0, remainingSlots)

    // Step 2: Validate file types
    let filesToUpload: File[] = []
    let invalidCount = 0
    let tooLargeCount = 0

    selectedFiles.forEach((file) => {
      const fileExtension = file.name.split('.').pop()?.toLowerCase() || ''
      if (file.size > MAX_FILE_SIZE_BYTES) {
        tooLargeCount++
        return
      }
      if (ALLOWED_EXTENSIONS.includes(fileExtension)) {
        filesToUpload.push(file)
      } else {
        invalidCount++
      }
    })

    // Notify user about invalid files
    if (invalidCount > 0) {
      toast.warn(
        String(
          t('ai.uploadSkippedInvalid', {
            count: invalidCount,
            allowed: ALLOWED_EXTENSIONS.join(', '),
          }),
        ),
      )
    }

    if (tooLargeCount > 0) {
      toast.warn(
        String(
          t('ai.uploadSkippedTooLarge', {
            count: tooLargeCount,
            maxSize: MAX_FILE_SIZE_MB,
          }),
        ),
      )
    }

    // Step 3: Upload valid files
    if (files.length > selectedFiles.length) {
      toast.warn(String(t('ai.uploadLimitExceeded', { count: remainingSlots })))
    }

    if (filesToUpload.length > remainingSlots) {
      toast.warn(String(t('ai.uploadLimitExceeded', { count: remainingSlots })))
      filesToUpload = filesToUpload.slice(0, remainingSlots)
    }

    if (filesToUpload.length > 0) {
      handleUploadFiles(filesToUpload)
    }

    // Clear input so same file can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  /**
   * Uploads files to the server
   *
   * Process:
   * 1. Create FormData with files
   * 2. Send POST request to /ai/assistant/files
   * 3. Receive files array with name and path from server
   * 4. Add files to attachedFiles state
   *
   * @param files - Array of files to upload
   */
  const handleUploadFiles = async (files: File[]) => {
    setIsUploading(true)
    const formData = new FormData()

    // Append each file to FormData
    files.forEach((file) => {
      formData.append('file', file)
    })

    try {
      const res = await fetch(`${apiUrl}ai/assistant/files`, {
        method: 'POST',
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: formData,
      })

      if (!res.ok) {
        throw new Error('File upload failed')
      }

      const data: { files: Array<{ name: string; path: string }> } = await res.json()

      // Add uploaded files to state
      setAttachedFiles((prev) => {
        const combined = [...prev, ...data.files]
        return combined.slice(0, MAX_FILES_PER_UPLOAD)
      })
      toast.success(String(t('ai.uploadSuccess', { count: files.length })))
    } catch (error) {
      console.info('File upload error:', error)
      toast.error('File upload failed. Please try again.')
    } finally {
      setIsUploading(false)
    }
  }

  /**
   * Removes a file from the attachments list
   * @param pathToRemove - Path of the file to remove
   */
  const handleRemoveFile = (pathToRemove: string) => {
    setAttachedFiles((prev) => prev.filter((file) => file.path !== pathToRemove))
    toast.info('Attachment removed.')
  }

  // ─────────────────────────────────────────────────────────────────────────────────────────────
  // CHAT MESSAGE HANDLER
  // ─────────────────────────────────────────────────────────────────────────────────────────────

  /**
   * Sends a message to the AI assistant
   *
   * Process Flow:
   * 1. Validate message/attachments
   * 2. Add user message to chat
   * 3. Send request to API with:
   *    - Message prompt
   *    - Conversation ID (for session tracking)
   *    - Previous response ID (for context)
   *    - File attachments (if any)
   * 4. Stream response from API
   * 5. Update UI in real-time as response arrives
   * 6. Save response ID for next message
   * 7. Clear attachments after successful send
   *
   * @param messageToSend - The message text to send
   */
  const handleSend = async (messageToSend: string) => {
    const trimmedMessage = messageToSend.trim()
    const attachmentsForMessage = attachedFiles.map((file) => ({ ...file }))

    // Validation: Don't send if already waiting for response or streaming
    if (isWaitingForResponse || isStreamingRef.current) {
      return
    }

    // Validation: Require either message text or attachments
    if (!trimmedMessage && attachmentsForMessage.length === 0) return

    // Clear input field
    setInputMessage('')
    // Reset textarea height
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto'
    }

    const promptPayload =
      trimmedMessage ||
      (attachmentsForMessage.length > 0 ? attachmentsForMessage.map((file) => file.name).join(', ') : '')

    const userMessageContent = trimmedMessage

    // Add user message to chat history
    setMessages((prev) => [
      ...prev,
      {
        message: userMessageContent,
        sender: 'user',
        direction: 'outgoing',
        attachments: attachmentsForMessage.length > 0 ? attachmentsForMessage : undefined,
      },
    ])

    // Auto-scroll to show new message
    scrollToBottom('auto')
    setIsWaitingForResponse(true)

    try {
      // Prepare request payload with new file structure
      const metaData = attachmentsForMessage.length > 0 ? { attachments: attachmentsForMessage } : null

      // Save conversation ID to localStorage when sending a message
      saveConversationId(conversationId)

      // Clear attachments immediately when sending request so UI resets for a new message
      setAttachedFiles([])
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }

      // Send request to AI chat endpoint
      const res = await fetch(`${apiUrl}ai/assistant/chats`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          prompt: promptPayload || 'File attachments',
          conversationId: conversationId, // Unique session ID
          previousResponseId: previousResponseId, // For maintaining context
          metaData: metaData, // File attachments
        }),
      })

      if (!res.ok) {
        const errorText = await res.text()

        // Try to parse error as JSON to check for specific error keys
        try {
          const errorJson = JSON.parse(errorText)
          // Check if error contains DAILY_LIMIT_REACHED key
          if (
            errorJson.error === 'DAILY_LIMIT_REACHED' ||
            errorJson.message === 'DAILY_LIMIT_REACHED' ||
            errorJson.key === 'DAILY_LIMIT_REACHED'
          ) {
            toast.error(String(t('ai.dailyLimitReached')))
            setIsWaitingForResponse(false)
            isStreamingRef.current = false
            return
          }
        } catch {
          // If parsing fails, check if error text contains the key
          if (errorText.includes('DAILY_LIMIT_REACHED')) {
            toast.error(String(t('ai.dailyLimitReached')))
            setIsWaitingForResponse(false)
            isStreamingRef.current = false
            return
          }
        }

        throw new Error(errorText || `Request failed with status ${res.status}`)
      }

      if (!res.body) throw new Error('Response body is empty')

      // ─── STREAM PROCESSING ─────────────────────────────────────────────────────────────────
      // Set up streaming response reader
      const reader = res.body.getReader()
      const decoder = new TextDecoder()

      let botText = '' // Accumulated bot response text
      let isFirstChunk = true // Track if this is the first chunk received
      isStreamingRef.current = true // Mark that we're streaming

      // Read stream until complete
      while (true) {
        const { done, value } = await reader.read()
        if (done) break

        // Decode chunk and split into lines
        const chunk = decoder.decode(value, { stream: true })
        const lines = chunk.split('\n')

        // Process each line in the chunk
        for (const line of lines) {
          // Look for Server-Sent Events format: "data: {json}"
          if (line.startsWith('data:')) {
            const json = line.replace(/^data:\s*/, '')
            if (!json || json === '[DONE]') continue

            try {
              const data = JSON.parse(json)

              // Handle streaming text delta
              if (data && data.type === 'delta' && typeof data.delta === 'string') {
                // Append new text to accumulated response
                botText += data.delta

                // Capture current text value to avoid closure issues in loop
                const currentText = botText

                // Update messages state with accumulated text
                setMessages((prev) => {
                  const copy = [...prev]

                  // If no bot message exists yet, create one
                  if (copy.length === 0 || copy[copy.length - 1].sender !== 'bot') {
                    return [...copy, { message: currentText, sender: 'bot', direction: 'incoming' } as ChatMessage]
                  }

                  // Update existing bot message
                  copy[copy.length - 1].message = currentText
                  return copy
                })

                // On first chunk, hide waiting indicator
                if (isFirstChunk) {
                  setIsWaitingForResponse(false)
                  isFirstChunk = false
                }

                // Small delay for smooth streaming animation
                await delay(STREAMING_DELAY_MS)
                // Scroll smoothly during streaming - use throttled scrolling to prevent shaking
                // Scroll more frequently but with better control
                if (botText.length % 30 === 0 || botText.length < 30) {
                  requestAnimationFrame(() => {
                    // Use auto for instant scroll during streaming to prevent lag
                    scrollToBottom('auto')
                  })
                }
              }

              // Handle completion event
              if (data && data.type === 'completed' && data.id) {
                // Save response ID for context in next message
                setPreviousResponseId(data.id)
                // Save conversation ID to ensure it's persisted
                saveConversationId(conversationId)
                // Mark streaming as complete
                isStreamingRef.current = false
                // Final scroll after completion
                requestAnimationFrame(() => {
                  scrollToBottom('smooth')
                })
              }
            } catch (err) {
              console.info('JSON parse error:', json, err)
            }
          }
        }
      }

      // Ensure streaming flag is reset even if stream ends without completion event
      isStreamingRef.current = false
    } catch (error) {
      // Handle errors
      setIsWaitingForResponse(false)
      isStreamingRef.current = false // Reset streaming flag on error

      // Remove empty bot message if error occurred
      setMessages((prev) => {
        if (prev.length > 0 && prev[prev.length - 1].message === '' && prev[prev.length - 1].sender === 'bot') {
          return prev.slice(0, -1)
        }
        return prev
      })

      console.info('Streaming error:', error)
      toast.error('An error occurred. Please try again later.')
    }
  }

  // ─────────────────────────────────────────────────────────────────────────────────────────────
  // EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────────────────────

  /**
   * Auto-scroll to bottom when new messages arrive
   * Only scroll if not currently streaming or waiting for response to prevent shaking
   * Streaming scroll is handled separately in the stream processing loop
   */
  useEffect(() => {
    // Use requestAnimationFrame to prevent layout thrashing
    // Don't scroll during streaming - streaming logic handles its own scrolling
    if (!isWaitingForResponse && !isStreamingRef.current) {
      requestAnimationFrame(() => {
        scrollToBottom('auto')
      })
    }
  }, [messages.length, isWaitingForResponse]) // Only depend on length, not content, to reduce re-renders

  /**
   * Close menu when clicking outside
   */
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false)
      }
    }

    if (isMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isMenuOpen])

  /**
   * Load last conversation on mount if available
   * This restores the chat when user refreshes the page
   * - If logged in and has saved conversationId: try to load it from server
   * - If loading fails or no saved ID: start a new chat
   * - If not logged in: start a new chat (but don't load from server)
   */
  useEffect(() => {
    const loadLastConversation = async () => {
      const savedConversationId = getLastConversationId()

      // Only try to load from server if user is logged in, has saved conversation ID, and no messages yet
      if (token && savedConversationId && messages.length === 0) {
        try {
          // Try to load the saved conversation from server silently (no error messages)
          await loadChat(savedConversationId, true)
          // If loading succeeds, conversationId is already saved in loadChat function
          return
        } catch (error) {
          // If loading fails (chat doesn't exist on server or error), silently start a new conversation
          // Don't show error message - just start fresh
          // Error is intentionally ignored here to provide silent fallback behavior
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const _ignored = error
          const newId = generateConversationId()
          setConversationId(newId)
          saveConversationId(newId)
          return
        }
      }

      // If not logged in or no saved conversation, ensure we have a conversationId saved
      // This handles the case where user is not logged in or starting fresh
      if (conversationId) {
        saveConversationId(conversationId)
      } else {
        // Fallback: generate new ID if somehow we don't have one
        const newId = generateConversationId()
        setConversationId(newId)
        saveConversationId(newId)
      }
    }

    loadLastConversation()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]) // Re-run if token changes (user logs in/out)

  // ─────────────────────────────────────────────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────────────────────────────────────────────

  return (
    <Container>
      <SlideContainer isSliding={showChats || showLibrary}>
        {/* ═══ MAIN CHAT VIEW ═══ */}
        <SlideView isActive={!showChats && !showLibrary} direction="left">
          {/* ═══ CHAT AREA ═══ */}
          <ChatArea ref={chatAreaRef} isEmpty={messages.length === 0}>
            {/* Render all messages */}
            {messages.map((m, i) =>
              m.sender === 'user' ? (
                <UserAssistantMessage key={i} message={m} />
              ) : (
                <AIAssistantMessage key={i} message={m} />
              ),
            )}

            {/* Typing indicator (3 animated dots) */}
            {isWaitingForResponse && (
              <MessageGroup>
                <TypingIndicatorWrapper>
                  <Dot />
                  <Dot />
                  <Dot />
                </TypingIndicatorWrapper>
              </MessageGroup>
            )}

            {/* Invisible element at bottom for auto-scroll */}
            <div ref={chatEndRef} />
          </ChatArea>
        </SlideView>

        {/* ═══ CHATS LIST VIEW ═══ */}
        <SlideView isActive={showChats} direction="right">
          <AIChats
            onBack={() => {
              setShowChats(false)
              setIsExpanded(false)
              setExpandedView(null)
            }}
            onChatSelect={loadChat}
            isVisible={showChats}
            onChatDeleted={(deletedConversationId) => {
              if (deletedConversationId === conversationId) {
                resetCurrentConversation()
              }
            }}
          />
        </SlideView>

        {/* ═══ LIBRARY VIEW ═══ */}
        <SlideView isActive={showLibrary} direction="right">
          <AILibrary
            onBack={() => {
              setShowLibrary(false)
              setIsExpanded(false)
              setExpandedView(null)
            }}
            isVisible={showLibrary}
          />
        </SlideView>
      </SlideContainer>

      {/* ═══ INPUT AREA - Always visible at bottom ═══ */}
      <InputContainer hasMessages={messages.length > 0 || showChats || showLibrary || hasDroppedDown}>
        {/* Display attached files before sending */}
        {attachedFiles.length > 0 && (
          <AttachmentList>
            {attachedFiles.map((file, index) => (
              <AttachmentBadge key={index}>
                <File size={16} />
                <span>{file.name || `File ${index + 1}`}</span>
                <IconButton aria-label="Remove file" onClick={() => handleRemoveFile(file.path)}>
                  <X size={14} />
                </IconButton>
              </AttachmentBadge>
            ))}
          </AttachmentList>
        )}

        <InputWrapper>
          {/* Hidden file input (triggered by button click) */}
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileSelect}
            style={{ display: 'none' }}
            multiple
            disabled={isUploading || isWaitingForResponse || hasReachedAttachmentLimit}
            accept={ACCEPTED_MIME_TYPES}
          />

          {/* Menu wrapper with dropdown */}
          <MenuWrapper ref={menuRef}>
            {/* File upload button */}
            <IconButton
              aria-label="Add file"
              data-role="add-file"
              onClick={() => {
                if (!isUploading && !isWaitingForResponse) {
                  setIsMenuOpen(!isMenuOpen)
                }
              }}
              disabled={isUploading || isWaitingForResponse}
            >
              {isUploading ? <Dot /> : <Plus size={18} />}
            </IconButton>

            {/* Dropdown menu */}
            <MenuDropdown isOpen={isMenuOpen}>
              <MenuItem
                onClick={() => {
                  startNewChat()
                  setIsMenuOpen(false)
                }}
              >
                <MenuItemIcon>
                  <MessageSquarePlus size={18} />
                </MenuItemIcon>
                <MenuItemText>{t('ai.newChat')}</MenuItemText>
              </MenuItem>

              <MenuDivider />

              <MenuItem
                onClick={() => {
                  setExpandedView('chats')
                  setIsExpanded(true)
                  setShowChats(true)
                  setShowLibrary(false)
                  setHasDroppedDown(true)
                  setIsMenuOpen(false)
                }}
              >
                <MenuItemIcon>
                  <MessagesSquare size={18} />
                </MenuItemIcon>
                <MenuItemText>{t('ai.chats')}</MenuItemText>
              </MenuItem>
              <MenuDivider />

              <MenuItem
                onClick={() => {
                  setExpandedView('library')
                  setIsExpanded(true)
                  setShowLibrary(true)
                  setShowChats(false)
                  setHasDroppedDown(true)
                  setIsMenuOpen(false)
                }}
              >
                <MenuItemIcon>
                  <Images size={18} />
                </MenuItemIcon>
                <MenuItemText>{t('ai.library')}</MenuItemText>
              </MenuItem>

              <MenuDivider />

              <MenuItem
                disabled={isUploading || isWaitingForResponse || hasReachedAttachmentLimit}
                onClick={() => {
                  if (hasReachedAttachmentLimit) return
                  fileInputRef.current?.click()
                  setIsMenuOpen(false)
                }}
              >
                <MenuItemIcon>
                  <Paperclip size={18} />
                </MenuItemIcon>
                <MenuItemText>{t('ai.addFiles')}</MenuItemText>
              </MenuItem>
            </MenuDropdown>
          </MenuWrapper>

          {/* Message input field */}
          <Input
            ref={textareaRef}
            placeholder={isUploading ? t('loading') : t('ai.placeholder')}
            value={inputMessage}
            onChange={(e) => {
              const newValue = e.target.value
              setInputMessage(newValue)
              // Auto-resize textarea
              e.target.style.height = 'auto'
              e.target.style.height = e.target.scrollHeight + 'px'
              // When user starts typing, collapse expanded view and return to chat
              if (newValue.length > 0 && isExpanded) {
                setIsExpanded(false)
                setShowChats(false)
                setShowLibrary(false)
              }
            }}
            onFocus={() => {
              // If input is empty and not expanded, expand to show last selected view
              // But only if there are no messages in the current conversation (don't expand when continuing an existing chat)
              if (!inputMessage.trim() && !isExpanded && expandedView && messages.length === 0) {
                setIsExpanded(true)
                if (expandedView === 'chats') {
                  setShowChats(true)
                  setShowLibrary(false)
                } else if (expandedView === 'library') {
                  setShowLibrary(true)
                  setShowChats(false)
                }
              }
            }}
            onKeyDown={(e) => {
              // Prevent send on Enter if waiting for response or streaming
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault()
                if (!isWaitingForResponse && !isStreamingRef.current) {
                  handleSend(inputMessage)
                }
              }
            }}
            disabled={isUploading}
            rows={1}
          />

          {/* Send button */}
          <SendButton
            aria-label="Send message"
            onClick={() => handleSend(inputMessage)}
            disabled={
              isWaitingForResponse ||
              isStreamingRef.current ||
              isUploading ||
              (!inputMessage.trim() && attachedFiles.length === 0)
            }
          >
            <ArrowUp size={18} />
          </SendButton>
        </InputWrapper>

        {/* Disclaimer text */}
        <Disclaimer>{t('ai.disclaimer')}</Disclaimer>
      </InputContainer>
    </Container>
  )
}

export default AIAssistant
